/**
 * Read-only "Slutförande" — work-order section 10 once the ärende is completed
 * (spec §1.10). Pure: the page resolves names (UID → displayName, component
 * keys → names) before rendering.
 */
import React from 'react';
import { CheckCircle } from 'lucide-react';
import { WORK_ORDER_LABELS, workOrderSectionTestId } from '../../workOrderSections';
import { SectionTitle } from './SectionTitle';
import './completionForm.css';

const L = WORK_ORDER_LABELS;

export interface CompletionSummaryCondition {
    value: number;
    comment?: string;
}

export interface CompletionSummaryProps {
    /** Stockholm-formatted completion date. */
    completedAt: string;
    /** Resolved display name; internal pages only. */
    completedByName?: string;
    comment?: string;
    conditions?: Record<string, CompletionSummaryCondition>;
    /** componentKey → display name for the condition rows. */
    componentNames: Record<string, string>;
    actualCost?: number;
    actualCostInvestmentPercentage?: number;
    /** Internal users only; the guest page passes false. */
    showCost: boolean;
}

function formatKr(amount: number): string {
    return Math.round(amount).toLocaleString('sv-SE');
}

export function CompletionSummary({
    completedAt,
    completedByName,
    comment,
    conditions,
    componentNames,
    actualCost,
    actualCostInvestmentPercentage,
    showCost,
}: CompletionSummaryProps) {
    const conditionEntries = Object.entries(conditions ?? {});
    const hasCost = showCost && typeof actualCost === 'number';
    const hasShare = hasCost && typeof actualCostInvestmentPercentage === 'number';

    return (
        <section className="wo-completion-summary wo-card" data-testid={workOrderSectionTestId('completion')}>
            <SectionTitle icon={CheckCircle} title={L.sectionCompletionSummary} />

            <div className="wo-completion-summary__row">
                <strong>{L.completedAt}</strong> {completedAt}
                {completedByName ? ` ${L.createdBy} ${completedByName}` : ''}
            </div>

            {comment && (
                <div className="wo-completion-summary__row">
                    <div className="small text-muted">{L.comment}</div>
                    <div className="wo-completion-summary__comment">{comment}</div>
                </div>
            )}

            {conditionEntries.length > 0 && (
                <div className="wo-completion-summary__row">
                    <div className="small text-muted">{L.condition}</div>
                    {conditionEntries.map(([key, condition]) => (
                        <div key={key} data-testid="wo-completion-summary-condition">
                            {componentNames[key] ?? 'Komponent'}: {condition.value}
                            {condition.comment && (
                                <div className="wo-completion-summary__comment small text-muted">{condition.comment}</div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {hasCost && (
                <div className="wo-completion-summary__row">
                    <strong>{L.actualCost}</strong> {formatKr(actualCost as number)} kr
                </div>
            )}
            {hasShare && (
                <div className="wo-completion-summary__row">
                    <strong>{L.investmentShare}</strong>{' '}
                    {actualCostInvestmentPercentage} % = {formatKr((actualCost as number) * (actualCostInvestmentPercentage as number) / 100)} kr
                </div>
            )}
        </section>
    );
}

export default CompletionSummary;
